import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Grid,
  Stack,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FancyPaper from "../components/FancyPaper";
import FancySearchConsumption from "../components/utils/FancySearchConsumption";
import { getRequestProductsPerProject } from "../services/exitTicket/ExitTicketService";
import { downloadProjectInventoryPdf } from "../services/projectInventory/ProjectInventoryService";
import { useApp } from "../hooks/useApp";

interface ProjectSupply {
  productId: number;
  productName: string;
  productCode: string;
  productCategory: string;
  productCost: number;
  totalQuantity: number;
}

const formatCRC = (value: number) =>
  value.toLocaleString("es-CR", {
    style: "currency",
    currency: "CRC",
    minimumFractionDigits: 2,
  });

// Mismo endpoint que ya usa "Retorno a Bodega" en Boletas de Salida para saber
// cuánto tiene disponible cada producto del proyecto (mr_ta_ticket_activity /
// mr_ta_ticket_project_transfer, neto de SALBOD/SALTALLSRV/CAMPRO/RETBOD) —
// acá se muestra directo, en un solo paso, sin tener que simular el retorno.
const ManagementProjectInventoryControlDetail = () => {
  const { setLoading, setErrorMsg } = useApp();
  const theme = useTheme();
  const { id, nameC } = useParams<{ id: string; nameC: string }>();
  const projectId = Number(id);
  const [supplies, setSupplies] = useState<ProjectSupply[]>([]);
  const [preFilter, setPreFilter] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const dataInit = async () => {
      setLoading && setLoading(true);
      try {
        const data: any = await getRequestProductsPerProject(projectId);
        setSupplies(Array.isArray(data) ? data : []);
      } catch (error: any) {
        setErrorMsg && setErrorMsg(error.message);
      } finally {
        setLoading && setLoading(false);
      }
    };
    dataInit();
    // eslint-disable-next-line
  }, [projectId]);

  // Mismo filtro que Consumo por proyecto: categoría (Herramientas / Insumos)
  // más búsqueda por código o descripción. Los datos ya están cargados, así
  // que se filtra en el navegador.
  const filteredSupplies = useMemo(() => {
    const normalized = appliedFilter.trim().toLowerCase();
    return supplies.filter((s) => {
      if (category && s.productCategory?.toUpperCase() !== category) {
        return false;
      }
      if (!normalized) return true;
      return (
        s.productCode?.toLowerCase().includes(normalized) ||
        s.productName?.toLowerCase().includes(normalized)
      );
    });
  }, [supplies, category, appliedFilter]);

  const totalCost = filteredSupplies.reduce(
    (acc, s) => acc + s.totalQuantity * (s.productCost ?? 0),
    0
  );

  // PDF con el membrete de la lista de personal (lo genera REPORT_API) con el
  // mismo filtro aplicado en pantalla, para hacer el inventario físico.
  const handleDownloadPdf = async () => {
    setLoading && setLoading(true);
    try {
      const safeName = (nameC ?? "proyecto").replace(/[\\/:*?"<>|]/g, "_");
      await downloadProjectInventoryPdf(
        projectId,
        `inventario_${safeName}.pdf`,
        category || undefined,
        appliedFilter.trim() || undefined
      );
    } catch (error: any) {
      setErrorMsg && setErrorMsg("No se pudo generar el PDF del inventario");
    } finally {
      setLoading && setLoading(false);
    }
  };

  return (
    <FancyPaper
      pagetitle={nameC}
      prevpage="Control de inventario de proyectos"
      linkprevpage="/dashboard/projectInventoryControl"
    >
      <Grid container spacing={4} sx={{ pb: 8 }} justifyContent={"flex-end"}>
        <Grid item xs={12}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-end", md: "center" }}
            justifyContent="flex-end"
          >
            <FancySearchConsumption
              preFilter={preFilter}
              setPreFilter={setPreFilter}
              handleFetchData={() => setAppliedFilter("")}
              handleApplyFilter={async () => setAppliedFilter(preFilter)}
              handleApplyFilterStatus={(selected: string) => setCategory(selected)}
            />
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={filteredSupplies.length === 0}
              sx={{ borderRadius: "8px!important", whiteSpace: "nowrap" }}
            >
              Descargar PDF
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">{"Código"}</TableCell>
              <TableCell align="center">{"Descripción"}</TableCell>
              <TableCell align="center">{"Categoría"}</TableCell>
              <TableCell align="center">{"Cantidad"}</TableCell>
              <TableCell align="center">{"Costo unitario"}</TableCell>
              <TableCell align="center">{"Costo total"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSupplies.length > 0 ? (
              filteredSupplies.map((s) => (
                <TableRow
                  key={s.productId}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="center">{s.productCode}</TableCell>
                  <TableCell align="center">{s.productName}</TableCell>
                  <TableCell align="center">{s.productCategory}</TableCell>
                  <TableCell align="center">{s.totalQuantity}</TableCell>
                  <TableCell align="center">
                    {formatCRC(s.productCost ?? 0)}
                  </TableCell>
                  <TableCell align="center">
                    {formatCRC(s.totalQuantity * (s.productCost ?? 0))}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  {supplies.length === 0
                    ? "Este proyecto no tiene productos asignados actualmente"
                    : "No hay productos para el filtro seleccionado"}
                </TableCell>
              </TableRow>
            )}
            {filteredSupplies.length > 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="right"
                  style={{
                    fontWeight: "bold",
                    backgroundColor: theme.palette.primary.light,
                    borderRadius: "10px",
                  }}
                >
                  Costo Total:
                </TableCell>
                <TableCell
                  align="center"
                  style={{
                    fontWeight: "bold",
                    backgroundColor: theme.palette.primary.light,
                    borderRadius: "10px",
                  }}
                >
                  {formatCRC(totalCost)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </FancyPaper>
  );
};

export default ManagementProjectInventoryControlDetail;
